import moment from 'moment';
import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import { cpiGapOf } from './clientCpiGap';
import type { CpiField } from './clientCpiGap';
import type { QueueClient } from '../../../types/client';

/**
 * 補資料 — the one follow-up on 重點跟進 that is not about a date.
 *
 * Shown only when the dated signals leave a slot free (the user's choice 🅰️,
 * 2026-09-29): a birthday or a renewal is never pushed off the card for it, and
 * there is at most one a day.
 *
 * Only a client ONE field away from a C.P.I. band is offered, because that is
 * the only kind of 「missing information」 that pays off on the spot — the card
 * says what answering buys, not what is missing.
 */

/** The fields the drawer's 補資料 card can answer in one tap or one date. 月入 is not one of them. */
export const FILL_IN_FIELDS: readonly CpiField[] = ['personality', 'marriageStatus', 'child', 'dob'];

/** 「暫時不知道」 hides the client from this slot for this long. */
export const FILL_IN_SNOOZE_DAYS = 30;

export type FactsFillIn = { client: QueueClient; field: CpiField };

export type FactsFillInInput = {
  clients: QueueClient[];
  adviserEmail?: string;
  adviserPersonality?: string | null;
  /** Clients already on the card for a dated reason — one line per person. */
  excludeIds?: Set<string>;
  /** clientId → ISO date the snooze ends. */
  snoozed?: Record<string, string>;
  now?: moment.MomentInput;
};

const IMPORTANCE_RANK: Record<string, number> = { 'V.VIP': 0, VIP: 1 };

/**
 * Who to ask about, in this order:
 *  1. a meeting coming up — the answer can simply be asked for there;
 *  2. met most recently — the adviser is likeliest to know it already;
 *  3. V.VIP, then VIP.
 */
export const pickFactsFillIn = (input: FactsFillInInput): FactsFillIn | null => {
  const { clients, adviserEmail, adviserPersonality, excludeIds, snoozed = {} } = input;
  const today = moment(input.now).startOf('day');

  const ranked = (clients || [])
    .filter(client => !excludeIds?.has(client._id))
    .filter(client => {
      const until = snoozed[client._id];
      return !until || !moment(until).isAfter(today);
    })
    .map(client => {
      const { plan } = cpiGapOf(client, { adviserEmail, adviserPersonality });
      if (plan.length !== 1 || !FILL_IN_FIELDS.includes(plan[0])) return null;
      const factsheet = adviserFactsheetOf(client, adviserEmail);
      const next = factsheet?.nextMeetingDate ? moment(factsheet.nextMeetingDate) : null;
      const last = factsheet?.latestMeetingDate ? moment(factsheet.latestMeetingDate) : null;
      return {
        fill: { client, field: plan[0] },
        upcoming: next && next.isValid() && !next.isBefore(today) ? next.diff(today, 'days') : Infinity,
        sinceMet: last && last.isValid() ? today.diff(last.clone().startOf('day'), 'days') : Infinity,
        importance: IMPORTANCE_RANK[factsheet?.importance || ''] ?? 2
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)
    .sort(
      (left, right) =>
        left.upcoming - right.upcoming || left.sinceMet - right.sinceMet || left.importance - right.importance
    );

  return ranked.length ? ranked[0].fill : null;
};

const SNOOZE_KEY = 'clientv2-fill-in-snooze';

/**
 * Snoozes live in this browser only. Like the tick boxes beside them, they are
 * a convenience for the adviser at this screen, not a record anyone else reads.
 */
export const readFillInSnoozes = (): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(SNOOZE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const snoozeFillIn = (clientId: string, now?: moment.MomentInput): Record<string, string> => {
  const today = moment(now).startOf('day');
  const next: Record<string, string> = {};
  // Expired entries are dropped on every write, so the map never grows past the
  // clients snoozed in the last month.
  Object.entries(readFillInSnoozes()).forEach(([id, until]) => {
    if (moment(until).isAfter(today)) next[id] = until;
  });
  next[clientId] = today.clone().add(FILL_IN_SNOOZE_DAYS, 'days').format('YYYY-MM-DD');
  try {
    window.localStorage.setItem(SNOOZE_KEY, JSON.stringify(next));
  } catch (error) {
    // Private mode or storage full: the snooze still holds for this visit.
  }
  return next;
};
